import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../../lib/auth'
import { useCollection, useDocument } from '../../lib/firestore'
import { useTourAccess } from '../../lib/tours'
import { useAirports } from '../../lib/airports'
import { formatDay } from '../../lib/format'
import { resolvedAccessLevel } from '../../types/models'
import type { Flight, Hotel, ItineraryItem, Tour, TourCrew, WithId } from '../../types/models'
import Icon from '../../components/Icon'
import MobileHeader, { MobileScreen, SectionLabel } from '../../components/mobile/MobileHeader'
import FlightCard from '../../components/FlightCard'
import Empty from '../../components/Empty'
import DeleteConfirm from '../../components/DeleteConfirm'
import DayItinerary from './DayItinerary'
import ItineraryDialog from '../../dialogs/ItineraryDialog'
import FlightDialog from '../../dialogs/FlightDialog'
import { HotelDialog } from '../../dialogs/TravelDialogs'
import CrewDialog from '../../dialogs/CrewDialog'
import { deleteFlight, deleteHotel, removeCrewMember } from '../../lib/mutations'

/* ============================================================================
   The tour's sub-pages on a phone.

   Each is a screen in its own right with its own nav bar, rather than the
   desktop tour page rendered narrow — which is what `/tours/:id/schedule` was
   doing, and why it looked like a squashed overview.
   ============================================================================ */

function useTourContext() {
  const { tourId } = useParams<{ tourId: string }>()
  const { user, uid } = useAuth()
  const { data: tour, loading } = useDocument<Tour>('tours', tourId)
  const access = useTourAccess(tour, uid)
  return { tourId, user, uid, tour, loading, canEdit: access?.canEdit ?? false, access }
}

function Missing() {
  return (
    <div style={{ padding: '30px var(--gutter)' }}>
      <Empty title="Tour not found" message="It may have been deleted." />
    </div>
  )
}

/* -------------------------------------------------------------- schedule */

export function MobileTourSchedule() {
  const { tourId, uid, tour, loading, canEdit } = useTourContext()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<WithId<ItineraryItem> | null>(null)

  if (loading) return <div style={{ padding: 30 }} />
  if (!tour) return <Missing />

  return (
    <>
      <MobileHeader
        title="Itinerary"
        back={`/tours/${tourId}`}
        action={
          canEdit
            ? { icon: 'plus', label: 'Add item', accent: true, onClick: () => setAdding(true) }
            : undefined
        }
      />
      <MobileScreen>
        <DayItinerary
          tour={tour}
          onOpenItem={canEdit ? setEditing : undefined}
          emptyAction={canEdit ? { label: 'Add the first item', onClick: () => setAdding(true) } : undefined}
        />
      </MobileScreen>

      {(adding || editing) && uid && (
        <ItineraryDialog
          open
          item={editing}
          tourId={tour.id}
          userId={uid}
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
        />
      )}
    </>
  )
}

/* ---------------------------------------------------------------- travel */

export function MobileTravel() {
  const { tourId, uid, tour, loading, canEdit } = useTourContext()
  useAirports()

  const flights = useCollection<Flight>('flights', tourId ? [where('tourId', '==', tourId)] : null)
  const hotels = useCollection<Hotel>('hotels', tourId ? [where('tourId', '==', tourId)] : null)
  const crew = useCollection<TourCrew>('tourCrew', tourId ? [where('tourId', '==', tourId)] : null)

  const [addingFlight, setAddingFlight] = useState(false)
  const [editingFlight, setEditingFlight] = useState<WithId<Flight> | null>(null)
  const [deletingFlight, setDeletingFlight] = useState<WithId<Flight> | null>(null)
  const [addingHotel, setAddingHotel] = useState(false)
  const [editingHotel, setEditingHotel] = useState<WithId<Hotel> | null>(null)
  const [deletingHotel, setDeletingHotel] = useState<WithId<Hotel> | null>(null)

  if (loading) return <div style={{ padding: 30 }} />
  if (!tour) return <Missing />

  return (
    <>
      <MobileHeader title="Travel" back={`/tours/${tourId}`} />
      <MobileScreen>
        <section className="stack" style={{ gap: 8 }}>
          <SectionLabel
            trailing={
              canEdit ? (
                <button
                  className="row t-caption accent"
                  style={{ gap: 4 }}
                  onClick={() => setAddingFlight(true)}
                >
                  <Icon name="plus" size={14} /> Add
                </button>
              ) : undefined
            }
          >
            Flights
          </SectionLabel>

          {flights.data.length === 0 ? (
            <p className="panel panel-pad t-caption">No flights on this tour.</p>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {[...flights.data]
                .sort((a, b) => a.departureTimeUTC.toMillis() - b.departureTimeUTC.toMillis())
                .map((flight) => (
                  <FlightCard
                    key={flight.id}
                    flight={flight}
                    crew={crew.data}
                    canEdit={canEdit}
                    onEdit={() => setEditingFlight(flight)}
                    onDelete={() => setDeletingFlight(flight)}
                  />
                ))}
            </div>
          )}
        </section>

        <section className="stack" style={{ gap: 8 }}>
          <SectionLabel
            trailing={
              canEdit ? (
                <button
                  className="row t-caption accent"
                  style={{ gap: 4 }}
                  onClick={() => setAddingHotel(true)}
                >
                  <Icon name="plus" size={14} /> Add
                </button>
              ) : undefined
            }
          >
            Hotels
          </SectionLabel>

          {hotels.data.length === 0 ? (
            <p className="panel panel-pad t-caption">No hotels on this tour.</p>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {[...hotels.data]
                .sort((a, b) => a.checkInDate.toMillis() - b.checkInDate.toMillis())
                .map((hotel) => {
                  const nights = Math.max(
                    1,
                    Math.round(
                      (hotel.checkOutDate.toMillis() - hotel.checkInDate.toMillis()) /
                        86_400_000,
                    ),
                  )
                  return (
                    <button
                      key={hotel.id}
                      className="panel row"
                      style={{ padding: 16, gap: 12, textAlign: 'left', width: '100%' }}
                      onClick={() => canEdit && setEditingHotel(hotel)}
                    >
                      <Icon name="bed" size={19} style={{ color: 'var(--accent-text)' }} />
                      <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
                        <span className="truncate" style={{ fontWeight: 600 }}>
                          {hotel.name}
                        </span>
                        <span className="t-caption truncate">{hotel.city}</span>
                        <span className="t-spec-caption faint">
                          {formatDay(hotel.checkInDate, hotel.timezone)} –{' '}
                          {formatDay(hotel.checkOutDate, hotel.timezone)}
                        </span>
                      </span>
                      <span className="chip" style={{ flex: 'none' }}>
                        {nights}n
                      </span>
                    </button>
                  )
                })}
            </div>
          )}
        </section>
      </MobileScreen>

      {deletingFlight && (
        <DeleteConfirm
          label={`${deletingFlight.origin} → ${deletingFlight.destination}`}
          onCancel={() => setDeletingFlight(null)}
          onConfirm={async () => {
            await deleteFlight(deletingFlight.id)
            setDeletingFlight(null)
          }}
        />
      )}

      {deletingHotel && (
        <DeleteConfirm
          label={deletingHotel.name}
          onCancel={() => setDeletingHotel(null)}
          onConfirm={async () => {
            await deleteHotel(deletingHotel.id)
            setDeletingHotel(null)
          }}
        />
      )}

      {(addingFlight || editingFlight) && uid && (
        <FlightDialog
          open
          flight={editingFlight}
          tourId={tour.id}
          ownerId={uid}
          onClose={() => {
            setAddingFlight(false)
            setEditingFlight(null)
          }}
        />
      )}

      {(addingHotel || editingHotel) && uid && (
        <HotelDialog
          open
          hotel={editingHotel}
          tourId={tour.id}
          ownerId={uid}
          onClose={() => {
            setAddingHotel(false)
            setEditingHotel(null)
          }}
        />
      )}
    </>
  )
}

/* ------------------------------------------------------------------ crew */

export function MobileCrew() {
  const { tourId, user, uid, tour, loading, access } = useTourContext()
  const canManage = access?.canManageCrew ?? false
  const crew = useCollection<TourCrew>('tourCrew', tourId ? [where('tourId', '==', tourId)] : null)

  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<WithId<TourCrew> | null>(null)
  const [deleting, setDeleting] = useState<WithId<TourCrew> | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  if (loading) return <div style={{ padding: 30 }} />
  if (!tour) return <Missing />

  // Anyone who hasn't accepted floats up — they're the ones needing action.
  const sorted = [...crew.data].sort((a, b) => {
    const aPending = a.status === 'accepted' ? 1 : 0
    const bPending = b.status === 'accepted' ? 1 : 0
    if (aPending !== bPending) return aPending - bPending
    return a.name.localeCompare(b.name)
  })

  return (
    <>
      <MobileHeader
        title="Crew"
        back={`/tours/${tourId}`}
        action={
          canManage
            ? { icon: 'plus', label: 'Add crew', accent: true, onClick: () => setAdding(true) }
            : undefined
        }
      />
      <MobileScreen>
        {!crew.loading && crew.data.length === 0 && (
          <Empty
            title="Nobody on this tour yet"
            message="Add the people travelling and invite them in."
            action={canManage ? { label: 'Add someone', onClick: () => setAdding(true) } : undefined}
          />
        )}

        {sorted.length > 0 && (
          <div className="stack" style={{ gap: 10 }}>
            {sorted.map((member) => {
              const level = resolvedAccessLevel(member)
              const open = openId === member.id
              return (
                <div key={member.id} className="panel" style={{ overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenId(open ? null : member.id)}
                    aria-expanded={open}
                    className="row"
                    style={{ width: '100%', padding: 16, gap: 12, textAlign: 'left' }}
                  >
                    <Avatar name={member.name} />
                    <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
                      <span className="truncate" style={{ fontWeight: 600 }}>
                        {member.name}
                      </span>
                      <span className="t-caption truncate">
                        {member.roles?.join(' · ') || 'No role set'}
                      </span>
                    </span>
                    {member.status !== 'accepted' && (
                      <span className="chip chip-warning">{member.status}</span>
                    )}
                  </button>

                  {open && (
                    <div
                      className="stack"
                      style={{
                        gap: 12,
                        padding: 16,
                        background: 'var(--panel-raised)',
                        borderTop: '1px solid var(--hairline)',
                      }}
                    >
                      {member.email && (
                        <a className="row accent" style={{ gap: 9 }} href={`mailto:${member.email}`}>
                          <Icon name="account" size={15} />
                          <span className="truncate">{member.email}</span>
                        </a>
                      )}
                      {member.phone && (
                        <a className="row accent" style={{ gap: 9 }} href={`tel:${member.phone}`}>
                          <Icon name="people" size={15} />
                          {member.phone}
                        </a>
                      )}
                      <div className="stack" style={{ gap: 1 }}>
                        <span className="t-eyebrow">Access</span>
                        <span className="t-caption">
                          {level === 'editor'
                            ? 'Can change anything on this tour.'
                            : "Sees the tour but can't change it."}
                        </span>
                      </div>

                      {member.status !== 'accepted' && member.invitationCode && (
                        <div
                          className="spread"
                          style={{
                            padding: '10px 14px',
                            borderRadius: 'var(--radius-control)',
                            background: 'var(--accent-wash)',
                          }}
                        >
                          <span className="t-eyebrow">Joining code</span>
                          <span
                            className="t-spec"
                            style={{ letterSpacing: 3, color: 'var(--accent-text)', fontWeight: 600 }}
                          >
                            {member.invitationCode}
                          </span>
                        </div>
                      )}

                      {canManage && (
                        <div className="row" style={{ gap: 8 }}>
                          <button
                            className="btn btn-secondary"
                            style={{ flex: 1 }}
                            onClick={() => setEditing(member)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ flex: 1 }}
                            onClick={() => setDeleting(member)}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </MobileScreen>

      {(adding || editing) && uid && (
        <CrewDialog
          open
          member={editing}
          tour={tour}
          inviter={{ id: uid, name: user?.displayName || user?.email || 'Someone' }}
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
        />
      )}

      {deleting && (
        <DeleteConfirm
          label={deleting.name}
          detail={`They lose access to ${tour.tourName} immediately.`}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await removeCrewMember(deleting)
            setDeleting(null)
          }}
        />
      )}
    </>
  )
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <span
      aria-hidden
      style={{
        display: 'grid',
        placeItems: 'center',
        width: 38,
        height: 38,
        flex: 'none',
        borderRadius: '50%',
        background: 'var(--accent-wash)',
        color: 'var(--accent-text)',
        fontWeight: 700,
        fontSize: '0.8rem',
      }}
    >
      {initials || '—'}
    </span>
  )
}
